import { createBrowserRouter, RouterProvider, Navigate, Outlet } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './lib/queryClient';
import { AuthProvider } from './contexts/AuthContext';
import { StoreProvider } from './contexts/StoreContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ToastProvider } from './components/ui/Alert';
import { DashboardLayout } from './components/layout/DashboardLayout';
import { NativeScroll } from './components/layout/NativeScroll';
import { RouteErrorBoundary } from './components/layout/RouteErrorBoundary';
import AuthPromptModal from './components/auth/AuthPromptModal';
import MobileWelcome from './components/auth/MobileWelcome';
import { PushNotificationsBridge } from './components/PushNotificationsBridge';

// Pages
import { StoreList } from './pages/stores/StoreList';
import { DashboardHome } from './pages/dashboard/DashboardHome';
import { ProductList } from './pages/products/ProductList';
import { ProductCreateRedirect } from './pages/products/ProductCreateRedirect';
import { ProductSort } from './pages/products/ProductSort';
import { ProductImportWizard } from './pages/products/ProductImportWizard';
import { ProductCodeList } from './pages/productCodes/ProductCodeList';
import { OrderList } from './pages/orders/OrderList';
import { OrderDetails } from './pages/orders/OrderDetails';
import { OrderCreate } from './pages/orders/OrderCreate';
import { CategoryList } from './pages/categories/CategoryList';
import { CouponList } from './pages/coupons/CouponList';
import { CouponForm } from './pages/coupons/CouponForm';
import { CustomerList } from './pages/customers/CustomerList';
import { CustomerDetails } from './pages/customers/CustomerDetails';
import { PaylinkList } from './pages/paylinks/PaylinkList';
import { PaylinkForm } from './pages/paylinks/PaylinkForm';
import { ReviewList } from './pages/reviews/ReviewList';
import { AnalyticsPage } from './pages/analytics/AnalyticsPage';
import { AnalyticalLinkList } from './pages/analyticalLinks/AnalyticalLinkList';
import { ShippingPage } from './pages/shipping/ShippingPage';
import { InventoryPage } from './pages/inventory/InventoryPage';
import { CartList } from './pages/carts/CartList';
import { UserList } from './pages/users/UserList';
import { SettingsPage } from './pages/settings/SettingsPage';
import { StoreDesignPage } from './pages/settings/StoreDesignPage';
import { TranslationsPage } from './pages/translations/TranslationsPage';
import { AppsPage } from './pages/apps/AppsPage';
import { AppAuthorizePage } from './pages/apps/AppAuthorizePage';
import { WalletPage } from './pages/wallet/WalletPage';
import { NewsletterPage } from './pages/newsletter/NewsletterPage';
import { ContactInbox } from './pages/contacts/ContactInbox';
import { ComplaintsPage } from './pages/complaints/ComplaintsPage';
import { MarketingPage } from './pages/marketing/MarketingPage';
import { SearchAnalyticsPage } from './pages/analytics/SearchAnalyticsPage';
import { LiveVisitorsPage } from './pages/analytics/LiveVisitorsPage';
import { BlogList } from './pages/blog/BlogList';
import { ExpensesPage } from './pages/expenses/ExpensesPage';
import { MenuManagement } from './pages/menus/MenuManagement';
import { ABTestingPage } from './pages/abTesting/ABTestingPage';
import { ImmersiveGlobe } from './pages/analytics/ImmersiveGlobe';
import { VerificationPage } from './pages/verification/VerificationPage';
import { ReferralsPage } from './pages/referrals/ReferralsPage';
import { isNative } from './lib/platform';


// Root layout exists only so PushNotificationsBridge can live inside the
// router (it needs useNavigate). It otherwise just renders the matched child.
function RootShell() {
  return (
    <>
      <Outlet />
      <PushNotificationsBridge />
    </>
  );
}

const router = createBrowserRouter(
  [
    {
      element: <RootShell />,
      children: [
    {
      path: '/',
      element: <NativeScroll><StoreList /></NativeScroll>,
      errorElement: <RouteErrorBoundary />,
    },
    {
      // App install consent screen. Sits above /:storeId because the merchant
      // has not picked a store yet when a third-party app links them in; the
      // backend forwards the public /apps/authorize URL here.
      path: '/apps/authorize',
      element: <AppAuthorizePage />,
      errorElement: <RouteErrorBoundary />,
    },
    {
      // Referral program. Account-scoped like /apps/authorize above: a referral
      // code belongs to the person, not to one of their stores, so it must sit
      // outside (and before) the /:storeId subtree.
      path: '/referrals',
      element: <NativeScroll><ReferralsPage /></NativeScroll>,
      errorElement: <RouteErrorBoundary />,
    },
    {
      path: '/:storeId',
      element: <DashboardLayout />,
      errorElement: <RouteErrorBoundary />,
      children: [
        { index: true, element: <DashboardHome /> },
        // Orders
        { path: 'orders', element: <OrderList /> },
        { path: 'orders/create', element: <OrderCreate /> },
        { path: 'orders/:orderId', element: <OrderDetails /> },
        // Products
        { path: 'products', element: <ProductList /> },
        { path: 'products/create', element: <ProductCreateRedirect /> },
        { path: 'products/import', element: <ProductImportWizard /> },
        { path: 'products/sort', element: <ProductSort /> },
        { path: 'products/:productId/codes', element: <ProductCodeList /> },
        // Categories
        { path: 'categories', element: <CategoryList /> },
        // Coupons
        { path: 'coupons', element: <CouponList /> },
        { path: 'coupons/create', element: <CouponForm /> },
        { path: 'coupons/:couponId', element: <CouponForm /> },
        // Customers
        { path: 'customers', element: <CustomerList /> },
        { path: 'customers/:customerId', element: <CustomerDetails /> },
        // Paylinks
        { path: 'paylinks', element: <PaylinkList /> },
        { path: 'paylinks/create', element: <PaylinkForm /> },
        { path: 'paylinks/:paylinkId', element: <PaylinkForm /> },
        // Reviews
        { path: 'reviews', element: <ReviewList /> },
        // Analytics
        { path: 'analytics', element: <AnalyticsPage /> },
        { path: 'analytics/search', element: <SearchAnalyticsPage /> },
        { path: 'analytics/live', element: <LiveVisitorsPage /> },
        { path: 'analytics/globe', element: <ImmersiveGlobe /> },
        // Blog
        { path: 'blog', element: <BlogList /> },
        // Expenses
        { path: 'expenses', element: <ExpensesPage /> },
        // Menus
        { path: 'menus', element: <MenuManagement /> },
        // A/B Testing
        { path: 'ab-testing', element: <ABTestingPage /> },
        // Marketing
        { path: 'marketing', element: <MarketingPage /> },
        // Analytical Links
        { path: 'analytical-links', element: <AnalyticalLinkList /> },
        // Shipping
        { path: 'shipping', element: <ShippingPage /> },
        // Inventory
        { path: 'inventory', element: <InventoryPage /> },
        // Abandoned Carts
        { path: 'carts', element: <CartList /> },
        // Team
        { path: 'users', element: <UserList /> },
        // Settings
        { path: 'settings', element: <SettingsPage /> },
        { path: 'design', element: <StoreDesignPage /> },
        // Pages — folded into Design as a tab; keep the URL working as a deep-link
        { path: 'pages', element: <Navigate to="../design?tab=pages" replace /> },
        // Translations
        { path: 'translations', element: <TranslationsPage /> },
        // Apps
        { path: 'apps', element: <AppsPage /> },
        // Wallet
        { path: 'wallet', element: <WalletPage /> },
        // Newsletter
        { path: 'newsletter', element: <NewsletterPage /> },
        // Contact Inbox
        { path: 'contacts', element: <ContactInbox /> },
        // Complaints
        { path: 'complaints', element: <ComplaintsPage /> },
        // Verification
        { path: 'verification', element: <VerificationPage /> },
      ],
    },
    {
      path: '*',
      element: <Navigate to="/" replace />,
    },
      ],
    },
  ],
  // Web is served at /dashboard/ on the host. Inside Capacitor the bundle is
  // served at the WebView root, so there is no /dashboard prefix.
  { basename: isNative ? '/' : '/dashboard' },
);

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <ToastProvider>
          <AuthProvider>
            <StoreProvider>
              <RouterProvider router={router} />
              <AuthPromptModal />
              <MobileWelcome />
            </StoreProvider>
          </AuthProvider>
        </ToastProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
